import { filterNamespacedProperties } from '@/helpers/DictHelpers'
import environment from '@/helpers/Environment'
import language from '@/language'
import type { I18nOverrides } from '@/language/types'
import type { Tenant } from '@/types/TenantConfig'
import deepmerge from 'deepmerge'
import type { LocaleMessages } from 'vue-i18n'

import { setObjectPropertyByPath, getObjectPropertyByPath } from '@/helpers/MiscellaneousHelpers'

const tenant: Tenant = getTenant()

// TODO Maybe deprecate this, and always use `environment` instead?
export default tenant

export function getTenant(): Tenant {
  const result: Partial<Tenant> = environment.webClient ?? {}

  if (environment.theme) {
    // TODO Does it matter that we're mutating window.tix.webClient?
    result.theme = environment.theme
  }

  return {
    ...result,
    ...(window.__mockTenantConfig as Tenant),
  }
}

/**
 * Gets the tenant-configured option for a component.
 */
export function getComponentOption(keyPath: string, defaultValue: any = false) {
  return getObjectPropertyByPath(tenant.componentOptions || {}, keyPath, defaultValue)
}

export const customContent: Dict<CustomContentBlock[]> = {}
if (tenant.customContent) {
  // Get templates that have been overridden in the portal strings database table.
  const overrides = filterNamespacedProperties('block:', environment.portalStrings)
  tenant.customContent.forEach((block) =>
    block.regions.forEach((region) => {
      if (overrides[block.key]) {
        block.template = `<div>${overrides[block.key]}</div>`
      }
      customContent[region] = customContent[region] || []
      customContent[region].push(block)
    }),
  )
}

export const defaultLanguage = tenant.defaultLanguage || 'en'

/**
 * Gets I18n strings including overrides from portal strings and seller metadata.
 */
export function tenantI18nStrings() {
  const tenantConfigOverrides = tenant.i18nOverrides || {}

  // TODO Add a language column to portal_string table.
  const portalOverrides = parseFlatI18nOverrides(environment.portalStrings, 'en')

  // TODO Move seller overrides to seller_string table or abstract portal_string to be more generic.
  const sellerOverrides = parseFlatI18nOverrides(environment.sellerMeta || {})

  // Ordered overrides; Later items take precedence.
  return [language, tenantConfigOverrides, portalOverrides, sellerOverrides].reduce((a, b) =>
    deepmerge(a, b),
  ) as Dict<LocaleMessages>
}

function parseFlatI18nOverrides(data: Dictionary, language?): I18nOverrides {
  const result = {}
  const overrides = filterNamespacedProperties('i18n:', data)

  Object.entries(overrides).forEach(([path, value]) => {
    setObjectPropertyByPath(result, path, value)
  })

  return language ? { [language]: result } : result
}
